<script lang="ts">
  import { dndzone, DndEvent } from "svelte-dnd-action"
  import { flip } from "svelte/animate"
  import { DotsSixVertical, ArrowsDownUp, FloppyDisk, Copy } from "phosphor-svelte"
  import { PlusCircle } from "svelte-feathers"
  import { formatCentsAsUsd } from "@util/money"
  import { post } from "@util/api"
  import { PackageType } from "@models/trips/PackageType"

  const flipDurationMs = 200

  export let packageTypes: PackageType[]
  export let newPath: string
  export let newPackageTypePath: string
  export let updateOrderPath: string
  export let editTripsAllowed: boolean
  export let accessCodesEnabled: boolean = false

  let editOn = false
  let copyFeedbackFor: number | null = null
  let copyFeedback = ""
  let dragging = false
  let dragDisabled = !editOn
  let dropTargetStyle = { backgroundColor: "#fffcf2", outline: "solid 1px #e5e5e5", border: "dashed 1.5px #A6A295" }

  // Do this so svelte-dnd-action doesn't log a runtime error.
  // This runtime error doesn't affect anything in practice but it does break acceptance tests.
  // I would say the library is using the wrong logging level.
  for (let pkgType of packageTypes) {
    if (pkgType.id === undefined) {
      pkgType.id = null
    }
  }

  function transformDraggedElement(draggedEl: HTMLElement) {
    let draggable = draggedEl.querySelector(".draggable-item")

    draggable.classList.add("bg-white")
    draggable.classList.add("border-gray-300")
    draggable.classList.add("shadow--sm")
    draggable.classList.add("shadow--gray-400")
  }

  // Something within the typings isn't letting me do DndEvent<Package>
  type PackageDndEvent = CustomEvent<DndEvent<any>>
  function handlePackageSort(pkgType: PackageType, e: PackageDndEvent, nowDragging: boolean) {
    dragging = nowDragging
    pkgType.packages = e.detail.items
    packageTypes = [...packageTypes]
  }

  function typeHasPackages(pkgType: PackageType): boolean {
    return pkgType.packages.length > 0
  }

  function showsAccessCode(pkgType: PackageType): boolean {
    return accessCodesEnabled && pkgType.requiresAccessCode === true && !!pkgType.accessCode
  }

  async function copyAccessCode(pkgType: PackageType) {
    try {
      if (!navigator.clipboard || !pkgType.accessCode) throw new Error("Clipboard unavailable")
      await navigator.clipboard.writeText(pkgType.accessCode)
      copyFeedback = "Copied!"
    } catch {
      copyFeedback = "Copy failed"
    }
    copyFeedbackFor = pkgType.id
    setTimeout(() => (copyFeedbackFor = null), 1500)
  }

  function startReorder(e: MouseEvent) {
    e.preventDefault()
    editOn = true
    dragDisabled = false
  }

  async function saveOrder(e: MouseEvent) {
    e.preventDefault()

    let response = await post(updateOrderPath, { packageTypes })

    if (response.ok) {
      editOn = false
      dragDisabled = true
    }
  }
</script>

<section class="section-setup" data-role="packages-table">
  <div class="header">
    <h2>Packages</h2>
    <div class="switcher">
      {#if editOn}
        <button data-role="save-order" type="submit" class="button legacy-with-icon" on:click|once={saveOrder}>
          <FloppyDisk size="16" />Save
        </button>
      {:else if editTripsAllowed}
        <a class="legacy-with-icon link-button-component" href={newPackageTypePath} data-role="add-package-type-link">
          <PlusCircle size="16" />Add Package Type
        </a>
        <a class="legacy-with-icon link-button-component" href={newPath} data-role="add-package-link">
          <PlusCircle size="16" />Add Package
        </a>
        <button data-role="start-reordering" class="button legacy-with-icon less-important" on:click|once={startReorder}>
          <ArrowsDownUp size="16" />Reorganize
        </button>
      {/if}
    </div>
  </div>

  <section>
    {#each packageTypes as pkgType (pkgType.id)}
      <div class="px-4 py-4" data-role="package-type-section">
        {#if packageTypes.length > 1 || packageTypes[0].url !== undefined}
          <div
            class="border-b pb-1 font-semibold flex items-center justify-between gap-4"
            data-role="package-type-heading"
            aria-label={pkgType.name}>
            {#if editOn || pkgType.url === undefined}
              <span data-role="package-type-name"
                >{pkgType.name}
                {#if pkgType.registerLimit}
                  - Limit: {pkgType.registerLimit}
                {/if}</span>
            {:else}
              <a class="font-semibold" href={pkgType.url} data-role="edit-package-type[{pkgType.id}]">
                <span data-role="package-type-name"
                  >{pkgType.name}
                  {#if pkgType.registerLimit}
                    - Limit: {pkgType.registerLimit}
                  {/if}</span>
              </a>
            {/if}
            {#if showsAccessCode(pkgType)}
              <span class="relative flex items-center gap-1 text-sm font-normal" data-role="package-type-access-code">
                Requires code:
                <span class="font-semibold" data-role="access-code-value">{pkgType.accessCode}</span>
                <button
                  type="button"
                  class="!min-h-0 !px-0 !bg-transparent !text-gray-700 hover:!text-gray-900"
                  title="Copy to clipboard"
                  data-role="copy-access-code"
                  on:click={() => copyAccessCode(pkgType)}>
                  <Copy size="16" />
                </button>
                {#if copyFeedbackFor === pkgType.id}
                  <span
                    class="absolute right-0 top-full mt-1 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white"
                    data-role="copy-access-code-feedback">{copyFeedback}</span>
                {/if}
              </span>
            {/if}
          </div>
        {/if}

        <div
          class="stack | min-block-10"
          class:bg-gray-100={!typeHasPackages(pkgType)}
          class:empty-package-type={!typeHasPackages(pkgType)}
          class:editing={editOn}
          aria-description="Click 'Reorganize' and drag packages here to assign them to this package."
          use:dndzone={{ items: pkgType.packages, dragDisabled, flipDurationMs, transformDraggedElement, dropTargetStyle }}
          on:consider={(e) => handlePackageSort(pkgType, e, true)}
          on:finalize={(e) => handlePackageSort(pkgType, e, false)}>
          {#if typeHasPackages(pkgType)}
            {#each pkgType.packages as pkg (pkg.id)}
              <div
                class="border-gray-200 border-b last:border-0"
                data-role="reorder-package[{pkg.id}]"
                style={dragDisabled ? "cursor: grab" : "cursor: grabbing"}
                aria-label={pkg.name}
                animate:flip={{ duration: flipDurationMs }}>
                <section class="draggable-item | row gap-inline-4 pad-block-2 | text-sm">
                  {#if editOn}
                    <span class="text-base flex">
                      <DotsSixVertical size="18" />
                    </span>
                  {/if}
                  <span class="text-base flex">
                    {#if editOn}
                      <span data-role="package-name">{pkg.name}</span>
                    {:else}
                      <a href={pkg.url} data-role="edit-package[{pkg.id}]">
                        <span data-role="package-name">{pkg.name}</span>
                      </a>
                    {/if}

                    {#if !pkg.visibleToPassengers}
                      <div class="inline ml-1 px-2 text-sm italic text-gray-700 bg-gray-300 rounded-sm py-2px">Hidden</div>
                    {/if}
                  </span>
                  <span class="flex-1 text-base flex justify-end">{formatCentsAsUsd(pkg.price)}</span>
                </section>
              </div>
            {/each}
          {/if}
        </div>
      </div>
    {/each}
  </section>
</section>

<style>
  .empty-package-type::after {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 12px;
    min-block-size: 72px;
    content: "Click 'Reorganize' and drag packages here to assign them to this package.";
  }
  .empty-package-type.editing::after {
    content: "Drag packages here to assign them to this package.";
  }
</style>
